import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, Button, Checkbox, Group, Select, Text, Title } from '@mantine/core';
import { ClipboardList, X } from 'lucide-react';
import { TodoList } from '../../types/todo';
import { todoApi } from '../../api/todoApi';
import { useUser } from '../../contexts/UserContext';
import { useNavigation } from '../../contexts/NavigationContext';

interface ConvertToTaskDialogProps {
  isOpen: boolean;
  noteTitle: string;
  noteContent: string;
  onClose: () => void;
  onConvert: (listId: number, deleteNote: boolean, createMultiple?: boolean, listColor?: string, listName?: string) => Promise<void>;
  selectedLines?: string[];
  hideDeleteOption?: boolean;
}

export const ConvertToTaskDialog: React.FC<ConvertToTaskDialogProps> = ({
  isOpen,
  noteTitle,
  noteContent,
  onClose,
  onConvert,
  selectedLines,
  hideDeleteOption,
}) => {
  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const [deleteNoteAfter, setDeleteNoteAfter] = useState(false);
  const [todoLists, setTodoLists] = useState<TodoList[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [createMultiple, setCreateMultiple] = useState(false);
  
  const { currentUser } = useUser();
  const { isMobile } = useNavigation();

  // Prevent body scroll when dialog is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && currentUser?.email) {
      setIsLoading(true);
      todoApi.getAllLists(currentUser.email)
        .then((lists) => {
          setTodoLists(lists);
          // Auto-select first list if available
          if (lists.length > 0) {
            setSelectedListId(lists[0].id.toString());
          }
        })
        .catch((error) => {
          console.error('Failed to load todo lists:', error);
          setTodoLists([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen, currentUser?.email]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConvert = async () => {
    if (!selectedListId) return;
    
    setIsConverting(true);
    try {
  const chosen = todoLists.find(l => l.id.toString() === selectedListId);
  const color = chosen?.color;
  await onConvert(parseInt(selectedListId), deleteNoteAfter, createMultiple, color || undefined, chosen?.name);
      onClose();
    } catch (error) {
      console.error('Failed to convert note to task:', error);
    } finally {
      setIsConverting(false);
    }
  };

  if (!isOpen) return null;

  const listOptions = todoLists.map(list => ({
    value: list.id.toString(),
    label: list.name
  }));

  const taskTitle = noteTitle || 'Untitled Task';
  const taskPreview = noteContent.length > 100 
    ? noteContent.substring(0, 100) + '...' 
    : noteContent;
  const nonEmptyLines = Array.isArray(selectedLines)
    ? selectedLines.filter(l => l.trim().length > 0)
    : [];

  const modalContent = (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10200,
        padding: '20px',
        // Ensure proper stacking above all notes and other elements
        isolation: 'isolate',
        // Prevent body scroll when dialog is open
        overflowY: 'auto'
      }}
      onClick={handleBackdropClick}
    >
      <Box
        style={{
          backgroundColor: 'var(--app-surface)',
          border: '1px solid var(--app-border)',
          borderRadius: '12px',
          padding: isMobile ? '20px' : '24px',
          minWidth: isMobile ? 'auto' : '400px',
          maxWidth: isMobile ? 'calc(100vw - 40px)' : '500px',
          width: '100%',
          maxHeight: isMobile ? 'calc(100vh - 40px)' : '90vh',
          overflow: 'auto',
          position: 'relative',
          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)',
          // Ensure dialog appears above all content with proper stacking context
          zIndex: 1
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <Group justify="space-between" align="center" mb="lg">
          <Group gap="sm">
            <ClipboardList size={20} color="var(--app-text-primary)" />
            <Title order={3} style={{ color: 'var(--app-text-primary)' }}>
              Convert To Task
            </Title>
          </Group>
          <Button
            variant="subtle"
            size="sm"
            onClick={onClose}
            style={{
              padding: '4px',
              minHeight: 'auto',
              width: '28px',
              height: '28px'
            }}
          >
            <X size={16} />
          </Button>
        </Group>

        {/* Task Preview */}
        <Box
          mb="md"
          p="sm"
          style={{
            backgroundColor: 'var(--app-bg)',
            border: '1px solid var(--app-border)',
            borderRadius: '8px'
          }}
        >
          {createMultiple && nonEmptyLines.length > 1 ? (
            <>
              <Text size="sm" fw={500} color="var(--app-text-secondary)" mb="xs">
                Tasks Preview ({nonEmptyLines.length}):
              </Text>
              <Box component="ul" style={{ margin: 0, paddingLeft: '18px' }}>
                {nonEmptyLines.slice(0, 10).map((line, idx) => (
                  <li key={idx}>
                    <Text size="sm" color="var(--app-text-primary)">{line}</Text>
                  </li>
                ))}
                {nonEmptyLines.length > 10 && (
                  <li>
                    <Text size="sm" color="var(--app-text-secondary)">…and {nonEmptyLines.length - 10} more</Text>
                  </li>
                )}
              </Box>
            </>
          ) : (
            <>
              <Text size="sm" fw={500} color="var(--app-text-secondary)" mb="xs">
                Task Preview:
              </Text>
              <Text size="sm" fw={600} color="var(--app-text-primary)" mb="xs">
                {taskTitle}
              </Text>
              {taskPreview && (
                <Text size="xs" color="var(--app-text-secondary)" style={{ lineHeight: 1.4 }}>
                  {taskPreview}
                </Text>
              )}
            </>
          )}
        </Box>

  {/* List Selection */}
        <Box mb="md">
          <Text size="sm" fw={500} mb="xs" color="var(--app-text-primary)">
            Add to List:
          </Text>
          {isLoading ? (
            <Text size="sm" color="var(--app-text-secondary)">
              Loading your lists...
            </Text>
          ) : todoLists.length === 0 ? (
            <Text size="sm" color="var(--app-text-secondary)">
              You don't have any todo lists yet. Create one first!
            </Text>
          ) : (
            <Select
              placeholder="Choose a list"
              data={listOptions}
              value={selectedListId}
              onChange={setSelectedListId}
              size="sm"
              styles={{
                input: {
                  backgroundColor: 'var(--app-surface)',
                  borderColor: 'var(--app-border)',
                  color: 'var(--app-text-primary)'
                }
              }}
            />
          )}
        </Box>

        {/* Multi-create option when multiple lines are selected */}
        {Array.isArray(selectedLines) && selectedLines.filter(l => l.trim().length > 0).length > 1 && (
          <Box mb="md">
            <Checkbox
              checked={createMultiple}
              onChange={(e) => setCreateMultiple(e.currentTarget.checked)}
              label={`Create ${selectedLines.filter(l => l.trim().length > 0).length} tasks (one per line)`}
              size="sm"
              styles={{
                label: { color: 'var(--app-text-primary)' }
              }}
            />
          </Box>
        )}

        {/* Delete Note Option - Hidden when requested */}
        {!hideDeleteOption && (
          <Box mb="md">
            <Checkbox
              checked={deleteNoteAfter}
              onChange={(event) => setDeleteNoteAfter(event.currentTarget.checked)}
              label="Delete this note after conversion"
              size="sm"
              styles={{
                root: {
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                },
                input: {
                  width: '18px',
                  height: '18px',
                  minWidth: '18px',
                  minHeight: '18px',
                  flexShrink: 0,
                  marginTop: '1px'
                },
                label: {
                  color: 'var(--app-text-primary)',
                  fontSize: '14px',
                  lineHeight: '18px'
                }
              }}
            />
          </Box>
        )}

        {/* Actions */}
        <Group justify="flex-end" gap="sm" mt="xl">
          <Button
            variant="subtle"
            onClick={onClose}
            size="sm"
            disabled={isConverting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConvert}
            size="sm"
            loading={isConverting}
            disabled={!selectedListId || todoLists.length === 0}
            style={{
              backgroundColor: 'var(--app-text-primary)',
              color: 'var(--app-surface)'
            }}
          >
            {createMultiple ? 'Convert to Tasks' : 'Convert to Task'}
          </Button>
        </Group>
      </Box>
    </div>
  );

  // Use Portal to render dialog at document body level to escape any stacking contexts
  return createPortal(modalContent, document.body);
};